import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import HeroSlider from '../../components/HeroSlider/HeroSlider';
import './Home.css';


const Home = () => {
  // Gallery navigation state
  const [galleryTranslate, setGalleryTranslate] = React.useState(0);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [enableTransition, setEnableTransition] = React.useState(true);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [lightboxImage, setLightboxImage] = React.useState('');

  // Open lightbox with selected image
  const openLightbox = (imageSrc) => {
    setLightboxImage(imageSrc);
    setLightboxOpen(true);
  };

  // Close lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage('');
  };

  // Gallery navigation functions with seamless infinite loop
  const slideLeft = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    
    const newTranslate = galleryTranslate + 380; // Move right (show previous images)
    
    // If we've scrolled past the start, we need to reset seamlessly
    // Each image is 380px (350px + 30px gap), 15 images = 5700px
    if (newTranslate > 0) {
      // First, complete the current slide with transition
      setGalleryTranslate(newTranslate);
      
      // After transition completes, instantly reset to the duplicate position
      setTimeout(() => {
        setEnableTransition(false); // Disable transition for instant jump
        setGalleryTranslate(-5700 + 380); // Jump to equivalent position in duplicate set
        
        // Re-enable transition after a brief moment
        setTimeout(() => {
          setEnableTransition(true);
          setIsAnimating(false);
        }, 50);
      }, 500);
    } else {
      setGalleryTranslate(newTranslate);
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  const slideRight = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    
    const newTranslate = galleryTranslate - 380; // Move left (show next images)
    
    // If we've scrolled past the end of the first set, reset seamlessly
    // Each image is 380px (350px + 30px gap), 15 images = 5700px
    if (newTranslate <= -5700) {
      // First, complete the current slide with transition
      setGalleryTranslate(newTranslate);
      
      // After transition completes, instantly reset to the start
      setTimeout(() => {
        setEnableTransition(false); // Disable transition for instant jump
        setGalleryTranslate(0); // Jump to start (equivalent position)
        
        // Re-enable transition after a brief moment
        setTimeout(() => {
          setEnableTransition(true);
          setIsAnimating(false);
        }, 50);
      }, 500);
    } else {
      setGalleryTranslate(newTranslate);
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  useEffect(() => {
    // AOS is already initialized in App.js, no need to refresh here
    
    // Video auto-pause functionality
    const handleVideoPlay = (event) => {
      const currentVideo = event.target;
      const allVideos = document.querySelectorAll('.testimonial-videos-section video');
      
      // Pause all other videos when one starts playing
      allVideos.forEach(video => {
        if (video !== currentVideo && !video.paused) {
          video.pause();
        }
      });
    };

    // Add event listeners to all videos
    const addVideoEventListeners = () => {
      const allVideos = document.querySelectorAll('.testimonial-videos-section video');
      allVideos.forEach(video => {
        video.addEventListener('play', handleVideoPlay);
      });
    };

    // Add event listeners after component mounts
    const timer = setTimeout(addVideoEventListeners, 100);

    // Add click handlers to all gallery images and project images
    const galleryImages = document.querySelectorAll('.gallery-slide-image');
    const projectImages = document.querySelectorAll('.project-image');
    const allImages = [...galleryImages, ...projectImages];
    
    const handleImageClick = (event) => {
      // Get the img element inside the clicked div
      const imgElement = event.currentTarget.querySelector('img');
      if (imgElement) {
        openLightbox(imgElement.src);
      }
    };
    
    // Add click handlers to gallery image containers
    allImages.forEach(container => {
      container.addEventListener('click', handleImageClick);
      container.style.cursor = 'pointer';
    });

    // Cleanup function
    return () => {
      clearTimeout(timer);
      const allVideos = document.querySelectorAll('.testimonial-videos-section video');
      allVideos.forEach(video => {
        video.removeEventListener('play', handleVideoPlay);
      });
      
      // Remove image click listeners
      allImages.forEach(container => {
        container.removeEventListener('click', handleImageClick);
      });
    };
  }, []);

  const services = [
    {
      id: 1,
      title: 'Residential Solar',
      description: 'Complete solar solutions for homes with high-efficiency panels and smart inverters. Save up to 90% on electricity bills with 3-5 years payback period.',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      icon: 'fas fa-home',
      features: ['3-5 Years Payback', '25 Years Warranty', 'Smart Monitoring', 'Net Metering']
    },
    {
      id: 2,
      title: 'Commercial Solar',
      description: 'Large-scale solar installations for businesses and industries. Reduce operational costs significantly with CAPEX & OPEX financing models.',
      image: 'https://images.unsplash.com/photo-1497440001374-f26997328c1b?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      icon: 'fas fa-building',
      features: ['CAPEX & OPEX Models', 'Tax Benefits', 'Bulk Pricing', 'Professional Installation']
    },
    {
      id: 3,
      title: 'Solar Maintenance',
      description: 'Professional maintenance and monitoring services to ensure optimal performance of your solar system with regular cleaning and inspections.',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      icon: 'fas fa-tools',
      features: ['Regular Cleaning', 'Performance Monitoring', '24/7 Support', 'Annual Inspections']
    },
    {
      id: 4,
      title: 'Government Subsidies',
      description: 'Complete assistance with government subsidies and documentation. Get maximum benefits available from central and state government schemes.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      icon: 'fas fa-file-alt',
      features: ['Subsidy Processing', 'Documentation Support', 'MNRE Registration', 'Approval Assistance']
    }
  ];

  const businessModels = [
    {
      id: 1,
      title: 'CAPEX Model',
      subtitle: 'Capital Expenditure',
      description: 'This is the most common business model for solar deployment in India. In this model, the consumer purchases the solar system by making 100% of the payment and owns the system.',
      icon: 'fas fa-coins',
      benefits: ['100% Ownership', 'Maximum Savings', 'Tax Benefits', 'Asset Value'],
      color: 'primary'
    },
    {
      id: 2,
      title: 'OPEX Model',
      subtitle: 'Power Purchase Agreement',
      description: 'Here the Global Solar Energy developer invests in the rooftop solar asset and sells the generated power to the building owner at a lower tariff. The excess power can be sold by the building owner to the utility through net metering.',
      icon: 'fas fa-handshake',
      benefits: ['Zero Investment', 'Immediate Savings', 'No Maintenance Cost', 'Flexible Terms'],
      color: 'secondary'
    }
  ];

  const benefits = [
    {
      icon: 'fas fa-leaf',
      title: 'Environmental Benefits',
      description: 'Reduce carbon emissions and contribute to a cleaner environment with 100% renewable energy source.'
    },
    {
      icon: 'fas fa-dollar-sign',
      title: 'Save Money',
      description: 'Save money on electricity bills with a payback time of 3 to 5 years and negligible running costs.'
    },
    {
      icon: 'fas fa-tools',
      title: 'Easy Installation',
      description: 'Easy to install with smart inverter technology and adjustable for any kind of relocation.'
    },
    {
      icon: 'fas fa-home',
      title: 'Increase Home Value',
      description: 'Solar installations increase property value and make your home more attractive to buyers.'
    },
    {
      icon: 'fas fa-shield-alt',
      title: '25 Year Life',
      description: 'Comprehensive warranty on all components with internal Wi-Fi monitoring system built-in.'
    },
    {
      icon: 'fas fa-star',
      title: 'Best Quality Materials',
      description: 'High-efficiency Make in India Solar Panels with premium smart inverters serving all in india.'
    }
  ];

  const counters = [
    { number: '5000+', label: 'Projects Completed', icon: 'fas fa-solar-panel' },
    { number: '4500+', label: 'Happy Customers', icon: 'fas fa-users' },
    { number: '15MW+', label: 'Solar Installed', icon: 'fas fa-bolt' },
    { number: '10+', label: 'Years Experience', icon: 'fas fa-award' }
  ];

  const projects = [
    {
      id: 1,
      title: 'Ajmera School',
      capacity: '12 KW',
      location: 'Amreli',
      type: 'Educational',
      image: '/images/Our work images/Amreli-Ajmera School 12 KW.PNG'
    },
    {
      id: 2,
      title: 'Balkrushna Cold Storage',
      capacity: '100 KW',
      location: 'Gondal',
      type: 'Commercial',
      image: '/images/Our work images/Gondal-Balkrushna Cold Storage 100 KW.PNG'
    },
    {
      id: 3,
      title: 'Krishna Industries',
      capacity: '100 KW',
      location: 'Industrial Area',
      type: 'Industrial',
      image: '/images/Our work images/Krishna Industries 100 KW.PNG'
    },
    {
      id: 4,
      title: 'Commercial Project',
      capacity: '25 KW',
      location: 'Sihor',
      type: 'Commercial',
      image: '/images/Our work images/Sihor Commercial 25 KW.PNG'
    },
    {
      id: 5,
      title: 'Vadera Novelty',
      capacity: '6 KW',
      location: 'Amreli',
      type: 'Residential',
      image: '/images/Our work images/Amreli-Vadera Novelty 6 KW.PNG'
    },
    {
      id: 6,
      title: 'Bagasara Project',
      capacity: '65 KW',
      location: 'Bagasara',
      type: 'Commercial',
      image: '/images/Our work images/Bagasara 65 KW.PNG'
    }
  ];

  const testimonials = [
    {
      id: 1,
      name: 'Hardik Dixit',
      position: '',
      location: '',
      text: 'Good service, Reliable product, Good customer support, Quick service',
      rating: 5,
      project: '',
      googleReviewUrl: 'https://www.google.com/search?kgmid=/g/11fx7rx9sd&hl=en-IN&q=Global+Solar+Energy&shndl=30&shem=damc,lcuae,ptotplc,shrtsdl&source=sh/x/loc/osrp/m5/1&kgs=28f162d7017c9ac8&utm_source=damc,lcuae,ptotplc,shrtsdl,sh/x/loc/osrp/m5/1&sei=VO9DaYr6K8iv4-EPotjKmAY#lrd='
    },
    {
      id: 2,
      name: 'Moin Ashrafi',
      position: '',
      location: '',
      text: 'Good',
      rating: 5,
      project: '',
      googleReviewUrl: 'https://www.google.com/search?kgmid=/g/11fx7rx9sd&hl=en-IN&q=Global+Solar+Energy&shndl=30&shem=damc,lcuae,ptotplc,shrtsdl&source=sh/x/loc/osrp/m5/1&kgs=28f162d7017c9ac8&utm_source=damc,lcuae,ptotplc,shrtsdl,sh/x/loc/osrp/m5/1&sei=VO9DaYr6K8iv4-EPotjKmAY#lrd='
    },
    {
      id: 3,
      name: 'Nkv Vadher',
      position: '',
      location: '',
      text: 'Good Service and customer support.Friendly behaviour of management which make any customer to ask about any doubts regarding service without hesitation.',
      rating: 5,
      project: '',
      googleReviewUrl: 'https://www.google.com/search?kgmid=/g/11fx7rx9sd&hl=en-IN&q=Global+Solar+Energy&shndl=30&shem=damc,lcuae,ptotplc,shrtsdl&source=sh/x/loc/osrp/m5/1&kgs=28f162d7017c9ac8&utm_source=damc,lcuae,ptotplc,shrtsdl,sh/x/loc/osrp/m5/1&sei=VO9DaYr6K8iv4-EPotjKmAY#lrd='
    },
    {
      id: 4,
      name: 'Mitesh Rajyaguru',
      position: '',
      location: '',
      text: 'Best service I received in time, supportive staff that coordinate greatly',
      rating: 5,
      project: '',
      googleReviewUrl: 'https://www.google.com/search?kgmid=/g/11fx7rx9sd&hl=en-IN&q=Global+Solar+Energy&shndl=30&shem=damc,lcuae,ptotplc,shrtsdl&source=sh/x/loc/osrp/m5/1&kgs=28f162d7017c9ac8&utm_source=damc,lcuae,ptotplc,shrtsdl,sh/x/loc/osrp/m5/1&sei=VO9DaYr6K8iv4-EPotjKmAY#lrd='
    },
    {
      id: 5,
      name: 'Vraj Paladiya',
      position: '',
      location: '',
      text: 'Very good solar plant Very good service & Very kind nature of all staff members',
      rating: 5,
      project: '',
      googleReviewUrl: 'https://www.google.com/search?kgmid=/g/11fx7rx9sd&hl=en-IN&q=Global+Solar+Energy&shndl=30&shem=damc,lcuae,ptotplc,shrtsdl&source=sh/x/loc/osrp/m5/1&kgs=28f162d7017c9ac8&utm_source=damc,lcuae,ptotplc,shrtsdl,sh/x/loc/osrp/m5/1&sei=VO9DaYr6K8iv4-EPotjKmAY#lrd='
    },
    {
      id: 6,
      name: 'Harshbhai Katariya',
      position: '',
      location: '',
      text: 'Global solar energy provide good installation and service for solar system',
      rating: 5,
      project: '',
      googleReviewUrl: 'https://www.google.com/search?kgmid=/g/11fx7rx9sd&hl=en-IN&q=Global+Solar+Energy&shndl=30&shem=damc,lcuae,ptotplc,shrtsdl&source=sh/x/loc/osrp/m5/1&kgs=28f162d7017c9ac8&utm_source=damc,lcuae,ptotplc,shrtsdl,sh/x/loc/osrp/m5/1&sei=VO9DaYr6K8iv4-EPotjKmAY#lrd='
    },
    {
      id: 7,
      name: 'Rohan Jethva',
      position: '',
      location: '',
      text: 'Such a good experience with global solar energy and best material quality. For rooftop solar I suggest every one to go with global solar.',
      rating: 5,
      project: '',
      googleReviewUrl: 'https://www.google.com/search?kgmid=/g/11fx7rx9sd&hl=en-IN&q=Global+Solar+Energy&shndl=30&shem=damc,lcuae,ptotplc,shrtsdl&source=sh/x/loc/osrp/m5/1&kgs=28f162d7017c9ac8&utm_source=damc,lcuae,ptotplc,shrtsdl,sh/x/loc/osrp/m5/1&sei=VO9DaYr6K8iv4-EPotjKmAY#lrd='
    },
    {
      id: 8,
      name: 'Gaurav Joshi',
      position: '',
      location: '',
      text: 'Sagar is providing the best solar solutions in Amreli. If you are looking for quick response, quality services and responsible after services, Global Solar Energy is the right place for you to go!!',
      rating: 5,
      project: '',
      googleReviewUrl: 'https://www.google.com/search?kgmid=/g/11fx7rx9sd&hl=en-IN&q=Global+Solar+Energy&shndl=30&shem=damc,lcuae,ptotplc,shrtsdl&source=sh/x/loc/osrp/m5/1&kgs=28f162d7017c9ac8&utm_source=damc,lcuae,ptotplc,shrtsdl,sh/x/loc/osrp/m5/1&sei=VO9DaYr6K8iv4-EPotjKmAY#lrd='
    }
  ];

  return (
    <>
      <Helmet>
        <title>Global Solar Energy - Think Solar for Better Future</title>
        <meta name="description" content="Leading solar energy solutions provider in Gujarat since 2015. We design, manufacture, and install high-efficiency solar systems for residential and commercial use." />
        <meta name="keywords" content="solar energy, solar panels, solar installation, renewable energy, Gujarat solar, Global Solar Energy, residential solar, commercial solar" />
      </Helmet>

      <div className="home-solafy">
        {/* Hero Slider */}
        <HeroSlider />

        {/* About Section */}
        <section className="section about-section">
          <div className="container">
            <div className="about-content about-content-full">
              <div className="about-main" data-aos="fade-up">
                <div className="section-badge">
                  <i className="fas fa-award"></i>
                  <span>Since 2015</span>
                </div>
                <h2>Leading Solar Energy Solutions Provider in Gujarat</h2>
                <p>
                  Global Solar Energy was founded in 2015 with the objective of designing,
                  manufacturing, and distributing world-class, high-efficiency solar products
                  under the brand name Global Solar Energy. We are committed to being the
                  global leader in innovation, design, engineering, and manufacturing of
                  high-quality products, especially in the solar industry.
                </p>
                <div className="about-features about-features-grid">
                  <div className="features-column">
                    <div className="feature-item">
                      <i className="fas fa-check-circle"></i>
                      <span>5MW+ Solar Installations Completed</span>
                    </div>
                    <div className="feature-item">
                      <i className="fas fa-check-circle"></i>
                      <span>Serving Gujarat, Maharashtra, Madhya Pradesh, Punjab</span>
                    </div>
                  </div>
                  <div className="features-column">
                    <div className="feature-item">
                      <i className="fas fa-check-circle"></i>
                      <span>Premium Australian Technology Smart Inverters</span>
                    </div>
                    <div className="feature-item">
                      <i className="fas fa-check-circle"></i>
                      <span>Make in India High-Efficiency Solar Panels</span>
                    </div>
                  </div>
                </div>
                <div className="about-button-center">
                  <Link to="/about" className="btn btn-primary">
                    <i className="fas fa-arrow-right"></i>
                    Learn More About Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section - Brand New Design */}
        <section className="solar-benefits-wrapper">
          <div className="benefits-container">
            <header className="benefits-header">
              <div className="section-badge">
                <i className="fas fa-star"></i>
                <span>Why Choose Solar</span>
              </div>
              <h2 className="benefits-title">Benefits of Solar Energy</h2>
              <p className="benefits-subtitle">Discover the amazing benefits of switching to clean, renewable solar energy</p>
            </header>
            <div className="benefits-cards-grid">
              {benefits.map((benefit, index) => (
                <article key={index} className="benefit-feature-card" data-aos="fade-up" data-aos-delay={index * 150}>
                  <div className="card-icon-wrapper">
                    <i className={benefit.icon}></i>
                  </div>
                  <div className="card-content">
                    <h3 className="card-title">{benefit.title}</h3>
                    <p className="card-description">{benefit.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="section section-alt services-section">
          <div className="container">
            <div className="section-header text-center" data-aos="fade-up">
              <div className="section-badge">
                <i className="fas fa-solar-panel"></i>
                <span>Our Services</span>
              </div>
              <h2>Complete Solar Energy Solutions</h2>
              <p>From design to installation and maintenance - we provide end-to-end solar solutions</p>
            </div>
            <div className="services-grid">
              {services.map((service, index) => (
                <div key={service.id} className="service-card" data-aos="fade-up" data-aos-delay={index * 100}>
                  <div className="service-image">
                    <img src={service.image} alt={service.title} />
                  </div>
                  <div className="service-content">
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                    <ul className="service-features">
                      {service.features.map((feature, idx) => (
                        <li key={idx}>
                          <i className="fas fa-check"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link to="/services" className="service-link">
                      Learn More <i className="fas fa-arrow-right"></i>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* Photo Gallery Section */}
        <section className="section photo-gallery-section">
          <div className="container">
            <div className="section-header text-center" data-aos="fade-up">
              <div className="section-badge">
                <i className="fas fa-camera"></i>
                <span>Photo Gallery</span>
              </div>
              <h2>Our Solar Installations</h2>
              <p>Browse through our completed solar projects and installations across Gujarat</p>
            </div>
            <div className="gallery-carousel">
              <div className="gallery-carousel-container">
                <div className="gallery-scrolling-wrapper">
                  <div className="gallery-scrolling-track" style={{
                    transform: `translateX(${galleryTranslate}px)`,
                    transition: enableTransition ? 'transform 0.5s ease' : 'none'
                  }}>
                    {/* First set of images */}
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image" >
                          <img src="/images/gallery/IMG_6335.PNG" alt="Solar Installation Project 1" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Panel Array</h4>
                          <p>Residential Installation</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6336.PNG" alt="Solar Installation Project 2" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Mounting System</h4>
                          <p>Commercial Setup</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6337.PNG" alt="Solar Installation Project 3" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Panel Installation</h4>
                          <p>Rooftop Project</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6338.PNG" alt="Solar Installation Project 4" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Array Setup</h4>
                          <p>Grid Connection</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6339.PNG" alt="Solar Installation Project 5" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Inverter</h4>
                          <p>Power Conversion Unit</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6340.PNG" alt="Solar Installation Project 6" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Panel Grid</h4>
                          <p>Large Scale Project</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6341.PNG" alt="Solar Installation Project 7" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Rooftop Solar System</h4>
                          <p>Home Installation</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6342.PNG" alt="Solar Installation Project 8" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Farm</h4>
                          <p>Industrial Scale</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6343.PNG" alt="Solar Installation Project 9" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Ground Mount Solar</h4>
                          <p>Utility Scale Project</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6344.PNG" alt="Solar Installation Project 10" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Installation</h4>
                          <p>Commercial Building</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6345.PNG" alt="Solar Installation Project 11" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Panel Array</h4>
                          <p>Business Complex</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6346.PNG" alt="Solar Installation Project 12" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Home Solar System</h4>
                          <p>Residential Project</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6347.PNG" alt="Solar Installation Project 13" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Monitoring System</h4>
                          <p>Smart Grid Integration</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6348.PNG" alt="Solar Installation Project 14" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Battery Storage</h4>
                          <p>Energy Storage System</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6349.PNG" alt="Solar Installation Project 15" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Maintenance</h4>
                          <p>Service & Support</p>
                        </div>
                      </div>
                    </div>
                    {/* Duplicate set for infinite scroll */}
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image" >
                          <img src="/images/gallery/IMG_6335.PNG" alt="Solar Installation Project 1" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Panel Array</h4>
                          <p>Residential Installation</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image" >
                          <img src="/images/gallery/IMG_6336.PNG" alt="Solar Installation Project 2" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Mounting System</h4>
                          <p>Commercial Setup</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image" >
                          <img src="/images/gallery/IMG_6337.PNG" alt="Solar Installation Project 3" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Panel Installation</h4>
                          <p>Rooftop Project</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6338.PNG" alt="Solar Installation Project 4" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Array Setup</h4>
                          <p>Grid Connection</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6339.PNG" alt="Solar Installation Project 5" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Inverter</h4>
                          <p>Power Conversion Unit</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6340.PNG" alt="Solar Installation Project 6" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Panel Grid</h4>
                          <p>Large Scale Project</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6341.PNG" alt="Solar Installation Project 7" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Rooftop Solar System</h4>
                          <p>Home Installation</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6342.PNG" alt="Solar Installation Project 8" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Farm</h4>
                          <p>Industrial Scale</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6343.PNG" alt="Solar Installation Project 9" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Ground Mount Solar</h4>
                          <p>Utility Scale Project</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6344.PNG" alt="Solar Installation Project 10" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Installation</h4>
                          <p>Commercial Building</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6345.PNG" alt="Solar Installation Project 11" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Panel Array</h4>
                          <p>Business Complex</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6346.PNG" alt="Solar Installation Project 12" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Home Solar System</h4>
                          <p>Residential Project</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6347.PNG" alt="Solar Installation Project 13" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Monitoring System</h4>
                          <p>Smart Grid Integration</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6348.PNG" alt="Solar Installation Project 14" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Battery Storage</h4>
                          <p>Energy Storage System</p>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-slide-item">
                      <div className="gallery-slide-card">
                        <div className="gallery-slide-image">
                          <img src="/images/gallery/IMG_6349.PNG" alt="Solar Installation Project 15" />
                        </div>
                        <div className="gallery-slide-overlay">
                          <h4>Solar Maintenance</h4>
                          <p>Service & Support</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
{/* Gallery Navigation Arrows */}
              <div className="gallery-navigation">
                <button
                  className="gallery-arrow gallery-arrow-left"
                  onClick={slideLeft}
                  disabled={isAnimating}
                  aria-label="Previous images"
                >
                  ‹
                </button>
                <button
                  className="gallery-arrow gallery-arrow-right"
                  onClick={slideRight}
                  disabled={isAnimating}
                  aria-label="Next images"
                >
                  ›
                </button>
              </div>
            </div>
          </div>
{/* Lightbox Modal */}
        {lightboxOpen && (
          <div className="lightbox-overlay" onClick={closeLightbox}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Close lightbox">
              ×
            </button>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <img src={lightboxImage} alt="Solar Installation" className="lightbox-image" />
            </div>
          </div>
        )}
        </section>

        {/* Business Models Section */}
        <section className="section business-models-section">
          <div className="container">
            <div className="section-header text-center" data-aos="fade-up">
              <div className="section-badge">
                <i className="fas fa-chart-line"></i>
                <span>Business Models</span>
              </div>
              <h2>Business Models We Offer</h2>
              <p>Choose the financing model that best suits your needs and budget</p>
            </div>
            <div className="business-models-grid">
              {businessModels.map((model, index) => (
                <div key={model.id} className={`business-model-card ${model.color}`} data-aos="fade-up" data-aos-delay={index * 200}>
                  <div className="model-icon">
                    <i className={model.icon}></i>
                  </div>
                  <div className="model-content">
                    <h3>{model.title}</h3>
                    <h4>{model.subtitle}</h4>
                    <p>{model.description}</p>
                    <ul className="model-benefits">
                      {model.benefits.map((benefit, idx) => (
                        <li key={idx}>
                          <i className="fas fa-check-circle"></i>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Counters Section */}
        <section className="section section-alt counters-section">
          <div className="container">
            <div className="counters-grid">
              {counters.map((counter, index) => (
                <div key={index} className="counter-card" data-aos="zoom-in" data-aos-delay={index * 100}>
                  <div className="counter-icon">
                    <i className={counter.icon}></i>
                  </div>
                  <div className="counter-content">
                    <h3 className={`counter-number ${counter.number.includes('MW') ? 'counter-mw' : ''}`}>{counter.number}</h3>
                    <p className="counter-label">{counter.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* Projects Section */}
        <section className="section section-alt projects-section">
          <div className="container">
            <div className="section-header text-center" data-aos="fade-up">
              <div className="section-badge">
                <i className="fas fa-images"></i>
                <span>Our Work</span>
              </div>
              <h2>Our Valuable Customers</h2>
              <p>Take a look at some of our successful solar installations across Gujarat</p>
            </div>
            <div className="projects-grid">
              {projects.map((project, index) => (
                <div key={project.id} className="project-card" data-aos="fade-up" data-aos-delay={index * 100}>
                  <div className="project-image">
                    <img src={project.image} alt={project.title} />
                    <div className="project-overlay">
                      <div className="project-info">
                        <span className="project-capacity">{project.capacity}</span>
                        <span className="project-type">{project.type}</span>
                      </div>
                    </div>
                  </div>
                  <div className="project-content">
                    <h3>{project.title}</h3>
                    <div className="project-meta">
                      <span><i className="fas fa-map-marker-alt"></i> {project.location}</span>
                      <span><i className="fas fa-bolt"></i> {project.capacity}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center" data-aos="fade-up" data-aos-delay="600">
              <Link to="/gallery" className="btn btn-outline">
                <i className="fas fa-images"></i>
                View All Projects
              </Link>
            </div>
          </div>
        </section>

        {/* Client Testimonial Videos Section */}
        <section className="section testimonial-videos-section">
          <div className="container">
            <div className="section-header text-center" data-aos="fade-up">
              <div className="section-badge">
                <i className="fas fa-video"></i>
                <span>Client Reviews</span>
              </div>
              <h2>What Our Customers Say</h2>
              <p>Watch real testimonials from our satisfied customers sharing their solar energy experience</p>
            </div>
            <div className="testimonial-videos-grid" data-aos="fade-up" data-aos-delay="200">
              {/* Video placeholders - you can replace these with actual video URLs */}
              <div className="video-testimonial-card first-video-card">
                <div className="video-container">
                  <video controls>
                    <source src="https://pub-24e933930fe34ffaa978daf5ee653936.r2.dev/WhatsApp%20Video%202025-12-19%20at%2015.11.59%20(1).mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
              
              <div className="video-testimonial-card">
                <div className="video-container">
                  <video controls>
                    <source src="https://pub-24e933930fe34ffaa978daf5ee653936.r2.dev/WhatsApp%20Video%202025-12-19%20at%2015.11.58.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
              
              <div className="video-testimonial-card">
                <div className="video-container">
                  <video controls>
                    <source src="https://pub-24e933930fe34ffaa978daf5ee653936.r2.dev/v1.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
              
              <div className="video-testimonial-card">
                <div className="video-container">
                  <video controls>
                    <source src="https://pub-24e933930fe34ffaa978daf5ee653936.r2.dev/WhatsApp%20Video%202025-12-19%20at%2015.11.59%20(2).mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
              
              <div className="video-testimonial-card">
                <div className="video-container">
                  <video controls>
                    <source src="https://pub-24e933930fe34ffaa978daf5ee653936.r2.dev/WhatsApp%20Video%202025-12-19%20at%2015.11.59.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
              
              <div className="video-testimonial-card">
                <div className="video-container">
                  <video controls>
                    <source src="https://pub-24e933930fe34ffaa978daf5ee653936.r2.dev/WhatsApp%20Video%202025-12-19%20at%2015.12.00%20(1).mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
              
              <div className="video-testimonial-card">
                <div className="video-container">
                  <video controls>
                    <source src="https://pub-24e933930fe34ffaa978daf5ee653936.r2.dev/WhatsApp%20Video%202025-12-19%20at%2015.12.00%20(2).mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
              
              <div className="video-testimonial-card">
                <div className="video-container">
                  <video controls>
                    <source src="https://pub-24e933930fe34ffaa978daf5ee653936.r2.dev/WhatsApp%20Video%202025-12-19%20at%2015.12.00.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
              
              <div className="video-testimonial-card">
                <div className="video-container">
                  <video controls>
                    <source src="https://pub-24e933930fe34ffaa978daf5ee653936.r2.dev/WhatsApp%20Video%202025-12-19%20at%2015.12.01%20(1).mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
              
              <div className="video-testimonial-card">
                <div className="video-container">
                  <video controls>
                    <source src="https://pub-24e933930fe34ffaa978daf5ee653936.r2.dev/WhatsApp%20Video%202025-12-19%20at%2015.12.01%20(2).mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
              
              <div className="video-testimonial-card">
                <div className="video-container">
                  <video controls>
                    <source src="https://pub-24e933930fe34ffaa978daf5ee653936.r2.dev/WhatsApp%20Video%202025-12-19%20at%2015.12.01.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="section testimonials-section">
          <div className="container">
            <div className="section-header text-center" data-aos="fade-up">
              <div className="section-badge">
                <i className="fas fa-comments"></i>
                <span>Testimonials</span>
              </div>
              <h2>What Our Customers Say</h2>
              <p>Hear from our satisfied customers who have switched to solar energy</p>
            </div>
            <div className="testimonials-container">
              {/* First Row - Scrolls Left to Right */}
              <div className="testimonials-row first-row">
                <div className="testimonials-track">
                  {testimonials.slice(0, 4).map((testimonial, index) => (
                    <div key={testimonial.id} className="testimonial-card">
                      <div className="testimonial-content">
                        <div className="testimonial-rating">
                          {[...Array(Math.floor(testimonial.rating))].map((_, i) => (
                            <i key={i} className="fas fa-star"></i>
                          ))}
                          {testimonial.rating % 1 !== 0 && (
                            <i className="fas fa-star-half-alt"></i>
                          )}
                          {[...Array(5 - Math.ceil(testimonial.rating))].map((_, i) => (
                            <i key={`empty-${i}`} className="far fa-star"></i>
                          ))}
                        </div>
                        <p>"{testimonial.text}"</p>
                      </div>
                      <div className="testimonial-author">
                        <div className="author-info">
                          <span>{testimonial.position}</span>
                          <span className="location">{testimonial.location}</span>
                        </div>
                      </div>
                      <div className="testimonial-google-review">
                        <h4>{testimonial.name}</h4>
                        <a
                          href={testimonial.googleReviewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="view-google-btn"
                        >
                          <i className="fab fa-google"></i>
                          View on Google
                        </a>
                      </div>
                    </div>
                  ))}
                  {/* Duplicate cards for seamless loop */}
                  {testimonials.slice(0, 4).map((testimonial, index) => (
                    <div key={`duplicate-1-${testimonial.id}`} className="testimonial-card">
                      <div className="testimonial-content">
                        <div className="testimonial-rating">
                          {[...Array(Math.floor(testimonial.rating))].map((_, i) => (
                            <i key={i} className="fas fa-star"></i>
                          ))}
                          {testimonial.rating % 1 !== 0 && (
                            <i className="fas fa-star-half-alt"></i>
                          )}
                          {[...Array(5 - Math.ceil(testimonial.rating))].map((_, i) => (
                            <i key={`empty-${i}`} className="far fa-star"></i>
                          ))}
                        </div>
                        <p>"{testimonial.text}"</p>
                      </div>
                      <div className="testimonial-author">
                        <div className="author-info">
                          <span>{testimonial.position}</span>
                          <span className="location">{testimonial.location}</span>
                        </div>
                      </div>
                      <div className="testimonial-google-review">
                        <h4>{testimonial.name}</h4>
                        <a
                          href={testimonial.googleReviewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="view-google-btn"
                        >
                          <i className="fab fa-google"></i>
                          View on Google
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Second Row - Scrolls Right to Left */}
              <div className="testimonials-row second-row">
                <div className="testimonials-track">
                  {testimonials.slice(4, 8).map((testimonial, index) => (
                    <div key={testimonial.id} className="testimonial-card">
                      <div className="testimonial-content">
                        <div className="testimonial-rating">
                          {[...Array(Math.floor(testimonial.rating))].map((_, i) => (
                            <i key={i} className="fas fa-star"></i>
                          ))}
                          {testimonial.rating % 1 !== 0 && (
                            <i className="fas fa-star-half-alt"></i>
                          )}
                          {[...Array(5 - Math.ceil(testimonial.rating))].map((_, i) => (
                            <i key={`empty-${i}`} className="far fa-star"></i>
                          ))}
                        </div>
                        <p>"{testimonial.text}"</p>
                      </div>
                      <div className="testimonial-author">
                        <div className="author-info">
                          <span>{testimonial.position}</span>
                          <span className="location">{testimonial.location}</span>
                        </div>
                      </div>
                      <div className="testimonial-google-review">
                        <h4>{testimonial.name}</h4>
                        <a
                          href={testimonial.googleReviewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="view-google-btn"
                        >
                          <i className="fab fa-google"></i>
                          View on Google
                        </a>
                      </div>
                    </div>
                  ))}
                  {/* Duplicate cards for seamless loop */}
                  {testimonials.slice(4, 8).map((testimonial, index) => (
                    <div key={`duplicate-2-${testimonial.id}`} className="testimonial-card">
                      <div className="testimonial-content">
                        <div className="testimonial-rating">
                          {[...Array(Math.floor(testimonial.rating))].map((_, i) => (
                            <i key={i} className="fas fa-star"></i>
                          ))}
                          {testimonial.rating % 1 !== 0 && (
                            <i className="fas fa-star-half-alt"></i>
                          )}
                          {[...Array(5 - Math.ceil(testimonial.rating))].map((_, i) => (
                            <i key={`empty-${i}`} className="far fa-star"></i>
                          ))}
                        </div>
                        <p>"{testimonial.text}"</p>
                      </div>
                      <div className="testimonial-author">
                        <div className="author-info">
                          <span>{testimonial.position}</span>
                          <span className="location">{testimonial.location}</span>
                        </div>
                      </div>
                      <div className="testimonial-google-review">
                        <h4>{testimonial.name}</h4>
                        <a
                          href={testimonial.googleReviewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="view-google-btn"
                        >
                          <i className="fab fa-google"></i>
                          View on Google
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section section-alt cta-section">
          <div className="container">
            <div className="cta-content text-center" data-aos="zoom-in">
              <h2>Ready to Switch to Solar Energy?</h2>
              <p>
                Get a free consultation and site analysis. Our experts will help you 
                design the perfect solar solution for your needs and budget.
              </p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-primary btn-lg">
                  <i className="fas fa-phone"></i>
                  Get Free Quote
                </Link>
                <a href="tel:+919724820358" className="btn btn-outline btn-lg">
                  <i className="fas fa-phone"></i>
                  Call: +91 97248 20358
                </a>
              </div>
              <div className="cta-features">
                <div className="cta-feature">
                  <span>Free Site Analysis</span>
                </div>
                <div className="cta-feature">
                  <span>Custom Solar Design</span>
                </div>
                <div className="cta-feature">
                  <span>Professional Installation</span>
                </div>
                <div className="cta-feature">
                  <span>Government Subsidy Support</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;